/**
 * Sidebar navigation, declared as data.
 *
 * Every item names the permission it needs. The shell filters this list against
 * the signed-in admin's effective permissions, so an operator only ever sees the
 * modules they can actually work in — and an empty section disappears entirely
 * rather than leaving a stray heading.
 *
 * This is presentation only. The backend enforces the same permission on every
 * endpoint; the route guard repeats it so a typed URL cannot reach a hidden page.
 */
import type { Accessor, Component } from "solid-js";
import LayoutDashboard from "lucide-solid/icons/layout-dashboard";
import ArrowLeftRight from "lucide-solid/icons/arrow-left-right";
import MessageSquare from "lucide-solid/icons/message-square";
import Users from "lucide-solid/icons/users";
import BadgeCheck from "lucide-solid/icons/badge-check";
import CandlestickChart from "lucide-solid/icons/candlestick-chart";
import Gift from "lucide-solid/icons/gift";
import Trophy from "lucide-solid/icons/trophy";
import Bell from "lucide-solid/icons/bell";
import ChartNoAxesColumn from "lucide-solid/icons/chart-no-axes-column";
import ShieldCheck from "lucide-solid/icons/shield-check";
import Settings from "lucide-solid/icons/settings";
import Gauge from "lucide-solid/icons/gauge";
import Landmark from "lucide-solid/icons/landmark";
import ScrollText from "lucide-solid/icons/scroll-text";
import Wallet from "lucide-solid/icons/wallet";
import Share2 from "lucide-solid/icons/share-2";

import type { Permission } from "~/core/rbac/permissions";
import { useChatUnreadBadge } from "~/features/chat/unread";

export interface NavItem {
  label: string;
  href: string;
  icon: Component<{ size?: number | string; class?: string }>;
  /** Permission required to see (and reach) this item. */
  permission: Permission;
  /** Exact-match the route (otherwise descendants also mark it active). */
  end?: boolean;
  /**
   * Optional count shown as a pill at the end of the row; hidden while it is 0.
   * This is a HOOK: the nav row calls it once in its own component scope, so it
   * may use queries/signals, and it only runs for admins who can see the item.
   */
  badge?: () => Accessor<number>;
  /** What the badge counts, for assistive tech — e.g. "unread conversations". */
  badgeLabel?: string;
}

export interface NavSection {
  /** Omit for the ungrouped block at the top. */
  heading?: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        label: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        permission: "dashboard:view",
      },
      {
        label: "Transactions",
        href: "/transactions",
        icon: ArrowLeftRight,
        permission: "transactions:view",
      },
      { label: "Control panel", href: "/control-panel", icon: Gauge, permission: "transactions:view" },
      {
        label: "Support chat",
        href: "/chat",
        icon: MessageSquare,
        permission: "chat:view",
        badge: useChatUnreadBadge,
        badgeLabel: "unread conversations",
      },
    ],
  },
  {
    heading: "Customers",
    items: [
      { label: "Customers", href: "/customers", icon: Users, permission: "users:view" },
      {
        label: "Verifications",
        href: "/verifications",
        icon: BadgeCheck,
        permission: "verifications:view",
      },
    ],
  },
  {
    heading: "Desk",
    items: [
      {
        label: "Markets",
        href: "/markets",
        icon: CandlestickChart,
        permission: "markets:view",
      },
      { label: "Rate engine", href: "/pricing", icon: Gauge, permission: "markets:view" },
      { label: "Reference rates", href: "/reference-rates", icon: Landmark, permission: "markets:view" },
      { label: "Bank accounts", href: "/bank-accounts", icon: Wallet, permission: "transactions:view" },
      { label: "Reports", href: "/reports", icon: ChartNoAxesColumn, permission: "reports:view" },
      { label: "Audit log", href: "/audit", icon: ScrollText, permission: "reports:view" },
    ],
  },
  {
    heading: "Growth",
    items: [
      { label: "Promotions", href: "/promotions", icon: Gift, permission: "promotions:view" },
      { label: "Challenges", href: "/challenges", icon: Trophy, permission: "challenges:view" },
      { label: "Referrals", href: "/referrals", icon: Share2, permission: "referrals:view" },
      {
        label: "Notifications",
        href: "/notifications",
        icon: Bell,
        permission: "notifications:view",
      },
    ],
  },
];

/** Rendered in the sidebar footer, away from day-to-day work. */
export const NAV_FOOTER: NavItem[] = [
  { label: "Team & roles", href: "/team", icon: ShieldCheck, permission: "staff:view" },
  { label: "Settings", href: "/settings", icon: Settings, permission: "config:view" },
];
